import React from 'react';
import { useParams, Link } from 'react-router-dom';
import PageShell from '../../components/PageShell';
import { getArticle, listArticles } from '../../lib/mockInsightArticles';
import { ArrowLeft, ArrowUpRight, Calendar, Clock } from 'lucide-react';

function Block({ b }) {
  if (b.type === 'h2') return <h2 className="mt-10 text-[24px] md:text-[26px] font-extrabold text-navy leading-snug">{b.content}</h2>;
  if (b.type === 'h3') return <h3 className="mt-8 text-[19px] md:text-[20px] font-bold text-navy leading-snug">{b.content}</h3>;
  if (b.type === 'p')  return <p className="mt-4 text-[15px] leading-[1.75] text-ink-700">{b.content}</p>;
  if (b.type === 'ul') return (
    <ul className="mt-4 space-y-2">
      {b.content.map((it, i) => (
        <li key={i} className="flex items-start gap-2 text-[15px] leading-[1.7] text-ink-700"><span className="mt-2.5 inline-block h-1.5 w-1.5 rounded-full bg-purple flex-shrink-0" />{it}</li>
      ))}
    </ul>
  );
  if (b.type === 'ref') return (
    <div className="mt-8 rounded-2xl border border-canvas-200 bg-canvas-100 p-5">
      <div className="text-[11.5px] font-bold tracking-widest text-purple">REFERENCES</div>
      <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-[12.5px] text-ink-500 leading-relaxed">
        {b.content.map((r, i) => (<li key={i}>{r}</li>))}
      </ol>
    </div>
  );
  return null;
}

export default function InsightArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return (
    <PageShell>
      <section className="px-6 pt-16 pb-24 mx-auto max-w-[900px] text-center">
        <h1 className="text-[32px] font-extrabold text-navy">Article not found</h1>
        <Link to="/insights" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2 text-[13px] font-semibold text-white"><ArrowLeft className="h-4 w-4" /> Back to Insights</Link>
      </section>
    </PageShell>
  );

  const initials = article.author.split(' ').map(s => s[0]).slice(0,2).join('');
  const related = listArticles().filter(a => a.slug !== slug).slice(0, 3);

  return (
    <PageShell>
      <article className="px-6 pt-12 pb-16">
        <div className="mx-auto max-w-[880px]">
          <Link to="/insights" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-purple hover:text-navy transition-colors"><ArrowLeft className="h-3.5 w-3.5" /> Insights and updates</Link>
          <h1 className="mt-5 text-[34px] md:text-[44px] font-extrabold leading-[1.1] tracking-tight text-navy">{article.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-3 text-[12.5px] text-ink-500">
            <span className="inline-flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-logo text-[10px] font-bold text-white">{initials}</span>
              <span className="font-semibold text-navy">{article.author}</span>
            </span>
            <span className="text-canvas-200">·</span>
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {article.date}</span>
            <span className="text-canvas-200">·</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.read}</span>
          </div>
        </div>

        <div className="mt-8 mx-auto max-w-[1080px]">
          <div className="overflow-hidden rounded-3xl border border-canvas-200 shadow-soft">
            <img src={article.cover} alt={article.title} className="block w-full object-cover max-h-[520px]" />
          </div>
          {article.coverCaption && (<div className="mx-auto mt-3 max-w-[880px] text-[12px] italic text-ink-500">{article.coverCaption}</div>)}
        </div>

        <div className="mx-auto mt-6 max-w-[880px]">
          {article.blocks.map((b, i) => (<Block key={i} b={b} />))}
        </div>
      </article>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <div className="text-[11.5px] font-bold tracking-widest text-purple">CONTINUE READING</div>
          <h2 className="mt-2 text-[24px] font-extrabold text-navy">More insights and updates</h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/insights/${r.slug}`} className="group overflow-hidden rounded-2xl border border-canvas-200 bg-white shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-card">
                <div className="aspect-[16/10] overflow-hidden"><img src={r.cover} alt={r.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" /></div>
                <div className="p-4">
                  <div className="text-[11.5px] text-ink-500">{r.date} · {r.read}</div>
                  <div className="mt-1 text-[15px] font-bold text-navy leading-snug group-hover:text-purple transition-colors">{r.title}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-purple">Read more <ArrowUpRight className="h-3.5 w-3.5" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
